import type { User } from "@supabase/supabase-js";
import { Badge, Group, Modal, Stack, Text, TextInput } from "@mantine/core";
import * as React from "react";
import { useNavigate } from "react-router";
import { resetAnalytics, track } from "../analytics/analytics";
import { deleteMyAccount } from "../api/account";
import { ApiError } from "../api/client";
import { signOut } from "../auth/signIn";
import { DangerButton, SecondaryButton } from "../ui/buttons";
import { Notice } from "../ui/Notice";
import { PageHeader } from "../ui/PageHeader";
import { SectionCard } from "../ui/SectionCard";
import { PANEL_SPACE } from "../ui/tokens";

const METHOD_LABEL: Record<string, string> = { email: "Email code", azure: "Microsoft", google: "Google" };

/** Sign-in methods linked to this account, from its Supabase identities. */
export function signInMethods(user: Pick<User, "identities" | "app_metadata">): string[] {
  const ids = (user.identities ?? []).map((i) => i.provider);
  const providers = ids.length > 0 ? ids : [String(user.app_metadata?.provider ?? "email")];
  return providers
    .filter((p, i) => providers.indexOf(p) === i)
    .map((p) => METHOD_LABEL[p] ?? p);
}

export function displayName(user: Pick<User, "user_metadata">): string | null {
  const meta = (user.user_metadata ?? {}) as Record<string, unknown>;
  const name = meta.full_name ?? meta.name;
  return typeof name === "string" && name.trim() ? name.trim() : null;
}

const CONFIRM_WORD = "delete";

/** Account (firm-model spec §10.2): name, email, linked sign-in methods, sign out, and
 * self-serve deletion with its consequences named (§5.4, R15). */
export function AccountPage({ user, remove = deleteMyAccount }: { user: User; remove?: () => Promise<void> }) {
  const navigate = useNavigate();
  const [confirming, setConfirming] = React.useState(false);
  const [typed, setTyped] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const onSignOut = async () => {
    await signOut();
    resetAnalytics();
    navigate("/sign-in", { replace: true });
  };

  const onDelete = async () => {
    setBusy(true);
    setError(null);
    try {
      await remove();
      track("web_account_deleted");
      await signOut();
      resetAnalytics();
      navigate("/deleted", { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "We couldn't delete your account. Try again.");
      setBusy(false);
    }
  };

  const name = displayName(user);
  return (
    <Stack gap={PANEL_SPACE.page}>
      <PageHeader title="Account" />
      <SectionCard title="Profile">
        <Stack gap={4}>
          {name ? <Text fw={600}>{name}</Text> : null}
          <Text>{user.email}</Text>
        </Stack>
        <Group gap="xs">
          <Text c="dimmed">Sign-In Methods:</Text>
          {signInMethods(user).map((m) => (
            <Badge key={m}>{m}</Badge>
          ))}
        </Group>
        <SecondaryButton onClick={() => void onSignOut()} w="fit-content">
          Sign out
        </SecondaryButton>
      </SectionCard>

      <SectionCard title="Delete My Account">
        <Text>
          This deletes your sign-in, your saved components and your usage history. If you're the only member of your firm,
          it also deletes the firm's workspace and cancels its subscription now. It can't be undone.
        </Text>
        <DangerButton onClick={() => setConfirming(true)} w="fit-content">
          Delete my account
        </DangerButton>
      </SectionCard>

      <Modal opened={confirming} onClose={() => !busy && setConfirming(false)} title="Delete your account?">
        <Stack gap="md">
          <Text>
            Your account, saved components and usage history are deleted now, and SlideBrain in PowerPoint won't sign you
            in with {user.email} again. This can't be undone.
          </Text>
          {error ? <Notice tone="danger">{error}</Notice> : null}
          <TextInput label={`Type "${CONFIRM_WORD}" to confirm`} value={typed} onChange={(e) => setTyped(e.currentTarget.value)} />
          <Group justify="flex-end" gap={5}>
            <SecondaryButton onClick={() => setConfirming(false)} disabled={busy}>
              Cancel
            </SecondaryButton>
            <DangerButton loading={busy} disabled={typed.trim().toLowerCase() !== CONFIRM_WORD} onClick={() => void onDelete()}>
              Delete my account
            </DangerButton>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
